import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, Table, Button, Spinner } from 'react-bootstrap';
import subscriptionService from '../../services/subscription.service';
import useFetch from '../../hooks/useFetch';

const InvoiceList = () => {
  const { currentUser } = useAuth();
  
  const { 
    data: invoicesData, 
    loading, 
    error, 
    refetch 
  } = useFetch(
    subscriptionService.getInvoices,
    [currentUser],
    {
      initialFetch: !!currentUser,
      initialData: []
    }
  );
  
  // Handle standardized API response format
  const invoices = invoicesData?.status === 'success' ? invoicesData.data.invoices || [] : [];
  
  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading invoices...</p>
        </Card.Body>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="mb-4 border-danger">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Your Invoices</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <div className="text-danger mb-3">
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '2rem' }}></i>
          </div>
          <p className="mb-2">{error}</p>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={refetch}
          >
            Try Again
          </Button>
        </Card.Body>
      </Card>
    );
  }
  
  return (
    <Card className="mb-4">
      <Card.Header className="bg-light">
        <h5 className="mb-0">Billing History</h5>
      </Card.Header>
      <Card.Body>
        {invoices.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-3 text-muted">
              <i className="bi bi-receipt" style={{ fontSize: '2.5rem' }}></i>
            </div>
            <p className="mb-1">No invoices found</p>
            <p className="text-muted small">Your billing history will appear here once you have made a payment.</p>
          </div>
        ) : (
          <Table responsive hover className="invoice-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(invoice => (
                <tr key={invoice.id}>
                  <td>{new Date(invoice.created * 1000).toLocaleDateString()}</td>
                  <td>${(invoice.amount_paid / 100).toFixed(2)} {invoice.currency.toUpperCase()}</td>
                  <td>
                    <span className={getStatusBadgeClass(invoice.status)}>
                      {formatStatus(invoice.status)}
                    </span>
                  </td>
                  <td>
                    {invoice.invoice_pdf && (
                      <Button 
                        href={invoice.invoice_pdf} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        variant="outline-secondary"
                        size="sm"
                      >
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        Download PDF
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

// Helper functions
const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'paid':
      return 'badge bg-success';
    case 'open':
      return 'badge bg-warning';
    case 'uncollectible':
      return 'badge bg-danger';
    default:
      return 'badge bg-secondary';
  }
};

const formatStatus = (status) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export default InvoiceList;
